// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Trash2, Plus, Download, Printer, MessageSquare, X } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const CYAN = "#06B6D4";

const fmtMoney = (n: any) =>
  n == null || n === "" || isNaN(Number(n))
    ? "$0"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(n));

const fmtDay = (s: any) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const fmtRange = (a: any, b: any) => {
  if (!a) return "";
  const f = (d: any) =>
    new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return b && b !== a ? `${f(a)}–${f(b)}` : f(a);
};

const randomColor = () => {
  const colors = [
    "#06B6D4", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444",
    "#3B82F6", "#EC4899", "#14B8A6", "#F97316", "#6366F1",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const initials = (name: string, email: string) => {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

const PAYMENT_STATUS_OPTIONS = [
  "unpaid",
  "partial",
  "paid",
  "refunded",
  "partially_refunded",
  "failed",
  "comped",
];
const PAY_TYPE_OPTIONS = ["online", "check", "cash", "venmo", "zelle"];

const statusBadgeClass = (s: string) => {
  switch (s) {
    case "paid":
      return "bg-green-100 text-green-800";
    case "comped":
      return "bg-cyan-100 text-cyan-800";
    case "partial":
      return "bg-amber-100 text-amber-800";
    case "refunded":
    case "partially_refunded":
      return "bg-orange-100 text-orange-800";
    case "failed":
      return "bg-red-100 text-red-800";
    case "unpaid":
      return "bg-slate-200 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

const payTypeBadgeClass = (s: string) => {
  switch (s) {
    case "online":
      return "bg-purple-100 text-purple-800";
    case "venmo":
      return "bg-blue-100 text-blue-800";
    case "zelle":
      return "bg-emerald-100 text-emerald-800";
    case "check":
    case "cash":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

/* ------------------------------------------------------------------ */
/* Columns                                                             */
/* ------------------------------------------------------------------ */

const COLS = [
  { key: "del", label: "", width: "w-10", editOnly: true },
  { key: "pool", label: "Pool", width: "w-24", dropdown: "pool" },
  { key: "division", label: "Div", width: "w-16", editable: true, field: "division", type: "text" },
  { key: "team_name", label: "Team", width: "w-48" },
  { key: "coach_first", label: "First", width: "w-24" },
  { key: "coach_last", label: "Last", width: "w-24" },
  { key: "email", label: "Email", width: "w-48", editable: true, field: "primary_billing_email", type: "text" },
  { key: "phone", label: "Phone", width: "w-32", editable: true, field: "coach_phone", type: "text" },
  { key: "schedule_request", label: "Sched Req", width: "w-24" },
  { key: "extras", label: "Extras", width: "w-32" },
  { key: "created_at", label: "Registered", width: "w-28" },
  { key: "payment_status", label: "Paid?", width: "w-24", dropdown: "paystatus" },
  { key: "total", label: "Total", width: "w-20", editable: true, field: "total_amount_due", type: "number" },
  { key: "paid", label: "Paid $", width: "w-20", editable: true, field: "total_amount_paid", type: "number" },
  { key: "owed", label: "Owed $", width: "w-20" },
  { key: "refunded", label: "Refunded $", width: "w-20" },
  { key: "scheduling_tier_fee", label: "Sched $", width: "w-20", editable: true, field: "scheduling_tier_fee", type: "number" },
  { key: "special_request_amount", label: "Spec Req $", width: "w-20", editable: true, field: "special_requests_fee", type: "number" },
  { key: "late_fee_charged", label: "Late $", width: "w-20" },
  { key: "pay_type", label: "Pay Type", width: "w-24", dropdown: "paytype" },
  { key: "roster", label: "Roster", width: "w-24" },
  { key: "notes", label: "💬", width: "w-12" },
];

/* ------------------------------------------------------------------ */
/* EmailGate                                                           */
/* ------------------------------------------------------------------ */

function EmailGate({ allowedEmails, eventName, onPassed }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const lower = email.toLowerCase().trim();
    if (!name.trim()) return setErr("Please enter your name.");
    if (!lower) return setErr("Please enter your email.");
    if (!allowedEmails.map((x) => x.toLowerCase()).includes(lower)) {
      return setErr("This email doesn't have access.");
    }
    localStorage.setItem("sheet_viewer_name", name.trim());
    localStorage.setItem("sheet_viewer_email", lower);
    onPassed();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-lg shadow-md p-6 space-y-4">
        <h1 className="text-lg font-semibold" style={{ color: CYAN }}>
          Verify your access
        </h1>
        <p className="text-sm text-slate-500">
          {eventName ? `"${eventName}" is ` : "This sheet is "}restricted. Enter the name
          and email this link was shared with.
        </p>
        <input
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Your email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {err && <p className="text-sm text-red-600">{err}</p>}
        <button
          type="submit"
          className="w-full h-10 rounded-md text-white font-medium"
          style={{ backgroundColor: CYAN }}
        >
          Continue
        </button>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PresenceBar                                                         */
/* ------------------------------------------------------------------ */

function PresenceBar({ viewers }) {
  if (!viewers?.length) return null;
  return (
    <div className="flex items-center -space-x-2">
      {viewers.slice(0, 6).map((u, i) => (
        <div
          key={(u.email || "") + i}
          title={u.name || u.email}
          className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white ring-2 ring-white"
          style={{ backgroundColor: u.color || "#64748b" }}
        >
          {initials(u.name, u.email)}
        </div>
      ))}
      {viewers.length > 6 && (
        <div className="h-7 w-7 rounded-full bg-slate-500 flex items-center justify-center text-[10px] font-semibold text-white ring-2 ring-white">
          +{viewers.length - 6}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* EditableCell                                                        */
/* ------------------------------------------------------------------ */

function EditableCell({ value, regId, field, type, permission, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (permission !== "edit") {
    if (type === "number") return <span className="truncate">{fmtMoney(value)}</span>;
    return <span className="truncate">{value ?? "—"}</span>;
  }

  const save = async () => {
    setEditing(false);
    const val = type === "number" ? parseFloat(draft) || 0 : draft;
    if (String(val ?? "") === String(value ?? "")) return;
    onUpdate(regId, field, val);
    const { error } = await supabase
      .from("event_registrations_v2")
      .update({ [field]: val })
      .eq("id", regId);
    if (error) onUpdate(regId, field, value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type === "number" ? "number" : "text"}
        step="0.01"
        className="w-full px-1 border rounded text-sm"
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }

  return (
    <span
      className="cursor-pointer hover:bg-cyan-50 px-1 block truncate"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
    >
      {type === "number" ? fmtMoney(value) : value ?? "—"}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* NotesCell                                                           */
/* ------------------------------------------------------------------ */

function NotesCell({ reg, eventId, tokenId, permission, onRefetch }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = reg.notes || [];
  const canWrite = permission === "comment" || permission === "edit";

  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    await supabase.from("event_sheet_notes").insert({
      registration_id: reg.id,
      event_id: eventId,
      token_id: tokenId,
      author_name: localStorage.getItem("sheet_viewer_name") || "Anonymous",
      note: draft.trim(),
    });
    setDraft("");
    setSaving(false);
    onRefetch();
  };

  return (
    <div className="relative">
      <button
        className="inline-flex items-center gap-1 text-slate-500 hover:text-cyan-600"
        onClick={() => setOpen((o) => !o)}
      >
        <MessageSquare className="h-4 w-4" />
        {notes.length > 0 && <span className="text-xs font-medium">{notes.length}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-md border bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">Notes</h4>
            <button onClick={() => setOpen(false)}>
              <X className="h-4 w-4 text-slate-400" />
            </button>
          </div>
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {notes.length === 0 && <p className="text-xs text-slate-400">No notes yet.</p>}
            {notes.map((n) => (
              <div key={n.id} className="rounded bg-slate-50 p-2">
                <p className="text-sm">{n.note}</p>
                <p className="mt-1 text-[10px] text-slate-400">
                  {n.author_name} · {n.created_at ? fmtDay(n.created_at) : ""}
                </p>
              </div>
            ))}
          </div>
          {canWrite && (
            <div className="mt-2 space-y-2">
              <textarea
                rows={2}
                className="w-full rounded border px-2 py-1 text-sm"
                placeholder="Add a note…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button
                disabled={saving}
                onClick={save}
                className="w-full h-8 rounded text-white text-sm font-medium"
                style={{ backgroundColor: CYAN }}
              >
                Add note
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Extras renderer                                                     */
/* ------------------------------------------------------------------ */

function ExtrasCell({ extras }) {
  const list = extras?.extras;
  if (!Array.isArray(list) || list.length === 0) return <span>—</span>;
  const full = list.map((e) => `${e.name} ×${e.qty ?? 1}`).join(", ");
  const shown = list.slice(0, 2).map((e) => `${e.name} ×${e.qty ?? 1}`).join(", ");
  const more = list.length > 2 ? ` +${list.length - 2} more` : "";
  return (
    <span title={full} className="truncate block">
      {shown}
      {more}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* AddTeamModal                                                        */
/* ------------------------------------------------------------------ */

function AddTeamModal({ token, poolOptions, onClose, onAdded }) {
  const [teamName, setTeamName] = useState("");
  const [coachName, setCoachName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pool, setPool] = useState("");
  const [totalDue, setTotalDue] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!teamName.trim()) return setErr("Team name is required.");
    setSaving(true);
    const { data: res, error: rpcErr } = await supabase.rpc("add_team_via_sheet_token", {
      p_token: token,
      p_team_name: teamName.trim(),
      p_coach_name: coachName.trim() || null,
      p_email: email.trim() || null,
      p_phone: phone.trim() || null,
      p_pool: pool || null,
      p_total_amount_due: parseFloat(totalDue) || 0,
    });
    setSaving(false);
    if (rpcErr) return setErr(rpcErr.message || "Failed to add team.");
    const parsed = typeof res === "string" ? JSON.parse(res) : res;
    if (parsed?.error) return setErr(parsed.error);
    await onAdded();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: CYAN }}>Add Team</h2>
          <button type="button" onClick={onClose}>
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>
        <div className="space-y-2">
          <input className="h-10 w-full rounded-md border px-3 text-sm" placeholder="Team name *" value={teamName} onChange={(e) => setTeamName(e.target.value)} autoFocus />
          <input className="h-10 w-full rounded-md border px-3 text-sm" placeholder="Coach name" value={coachName} onChange={(e) => setCoachName(e.target.value)} />
          <input className="h-10 w-full rounded-md border px-3 text-sm" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="h-10 w-full rounded-md border px-3 text-sm" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={pool} onChange={(e) => setPool(e.target.value)}>
            <option value="">Pool —</option>
            {poolOptions.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input className="h-10 w-full rounded-md border px-3 text-sm" placeholder="Total amount due" type="number" step="0.01" value={totalDue} onChange={(e) => setTotalDue(e.target.value)} />
        </div>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="h-10 rounded-md border px-4 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="h-10 rounded-md px-4 text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: CYAN }}>
            {saving ? "Adding…" : "Add Team"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function EventSheetPage({ tokenOverride, embedded = false }: { tokenOverride?: string; embedded?: boolean } = {}) {
  const params = useParams<{ token: string }>();
  const token = tokenOverride ?? params.token;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gatePassed, setGatePassed] = useState(false);

  const [search, setSearch] = useState("");
  const [poolFilter, setPoolFilter] = useState("All");
  const [paidFilter, setPaidFilter] = useState("All");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [viewers, setViewers] = useState<any[]>([]);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [showAddTeam, setShowAddTeam] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    const { data: result, error: rpcErr } = await supabase.rpc("get_event_sheet_data", {
      p_token: token,
    });
    if (rpcErr) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    const parsed = typeof result === "string" ? JSON.parse(result) : result;
    if (parsed?.error) {
      setError("This link is invalid or has expired.");
      setLoading(false);
      return;
    }
    setError(null);
    setData(parsed);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const permission = data?.permission || "view";
  const eventId = data?.event?.id;

  /* Realtime */
  useEffect(() => {
    if (!eventId) return;
    const channel = supabase
      .channel(`sheet-realtime-${eventId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_registrations_v2", filter: `event_id=eq.${eventId}` },
        () => fetchData()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, fetchData]);

  /* Presence */
  const needsGate = useMemo(() => {
    if (!data) return false;
    const allowed = data.allowed_emails;
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    if (gatePassed) return false;
    const stored = (localStorage.getItem("sheet_viewer_email") || "").toLowerCase();
    if (stored && allowed.map((e) => e.toLowerCase()).includes(stored)) return false;
    return true;
  }, [data, gatePassed]);

  useEffect(() => {
    if (!token || !data || needsGate) return;
    const viewerEmail =
      localStorage.getItem("sheet_viewer_email") ||
      `anon-${Math.random().toString(36).slice(2, 8)}`;
    const viewerName = localStorage.getItem("sheet_viewer_name") || "Guest";
    const color = randomColor();
    const ch = supabase.channel(`sheet-presence-${token}`, {
      config: { presence: { key: viewerEmail } },
    });
    ch.on("presence", { event: "sync" }, () => {
      setViewers(Object.values(ch.presenceState()).flat());
    }).subscribe(async (s) => {
      if (s === "SUBSCRIBED") await ch.track({ name: viewerName, email: viewerEmail, color });
    });
    return () => {
      supabase.removeChannel(ch);
    };
  }, [token, data, needsGate]);

  /* Optimistic local merge */
  const onUpdate = useCallback((regId, field, value) => {
    setData((prev) => {
      if (!prev) return prev;
      const map = {
        pool: "pool",
        division: "division",
        primary_billing_email: "email",
        coach_phone: "phone",
        total_amount_due: "total",
        total_amount_paid: "paid",
        scheduling_tier_fee: "scheduling_tier_fee",
        special_requests_fee: "special_request_amount",
        payment_status: "payment_status",
        registration_status: "status",
        payment_method_chosen: "pay_type",
      };
      const localKey = map[field] || field;
      return {
        ...prev,
        registrations: prev.registrations.map((r) =>
          r.id === regId
            ? {
                ...r,
                [localKey]: value,
                owed:
                  localKey === "total" || localKey === "paid"
                    ? (localKey === "total" ? value : r.total) - (localKey === "paid" ? value : r.paid)
                    : r.owed,
              }
            : r
        ),
      };
    });
  }, []);

  const updateDropdown = async (regId, field, localKey, value) => {
    onUpdate(regId, localKey === "pay_type" ? "payment_method_chosen" : field, value);
    await supabase.from("event_registrations_v2").update({ [field]: value }).eq("id", regId);
  };

  const deleteRow = async (regId) => {
    setConfirmDel(null);
    setData((prev) => ({
      ...prev,
      registrations: prev.registrations.filter((r) => r.id !== regId),
    }));
    const { data: gone, error } = await supabase.from("event_registrations_v2").delete().eq("id", regId).select("id");
    if (error || !gone?.length) {
      console.error("delete registration failed", error);
      window.alert(error?.message || "This team couldn't be deleted. You may not have permission to delete it.");
    }
    fetchData();
  };

  /* Derived: filtering + sorting */
  const registrations = data?.registrations || [];

  const poolOptions = useMemo(() => {
    const opts = data?.pool_options;
    return Array.isArray(opts) ? opts : [];
  }, [data]);

  const pools = useMemo(() => ["All", ...poolOptions], [poolOptions]);

  const filtered = useMemo(() => {
    let rows = [...registrations];
    const q = search.toLowerCase().trim();
    if (q) {
      rows = rows.filter((r) =>
        [r.team_name, r.coach_first, r.coach_last, r.email]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    if (poolFilter !== "All") rows = rows.filter((r) => r.pool === poolFilter);
    if (paidFilter !== "All") rows = rows.filter((r) => r.payment_status === paidFilter);

    if (sortKey) {
      const getVal = (reg, key) => {
        if (["total", "paid", "owed", "scheduling_tier_fee"].includes(key))
          return Number(reg[key]) || 0;
        return String(reg[key] ?? "").toLowerCase();
      };
      rows.sort((a, b) => {
        const av = getVal(a, sortKey);
        const bv = getVal(b, sortKey);
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [registrations, search, poolFilter, paidFilter, sortKey, sortDir]);

  /* Summary */
  const summary = useMemo(() => {
    const paidCount = registrations.filter((r) => r.payment_status === "paid").length;
    const compedCount = registrations.filter((r) => r.payment_status === "comped").length;
    const pendingCount = registrations.filter((r) => r.payment_status === "pending").length;
    const collected = registrations.reduce((s, r) => s + (Number(r.paid) || 0), 0);
    const owed = registrations.reduce((s, r) => s + (Number(r.owed) || 0), 0);
    const rostersReady = registrations.filter((r) => (r.roster_player_count || 0) > 0).length;
    const rostersPending = registrations.filter((r) => !(r.roster_player_count > 0)).length;
    const byPool = {};
    registrations.forEach((r) => {
      const p = r.pool || "—";
      byPool[p] = (byPool[p] || 0) + 1;
    });
    return {
      teams: registrations.length,
      paidCount,
      compedCount,
      pendingCount,
      collected,
      owed,
      rostersReady,
      rostersPending,
      byPool,
    };
  }, [registrations]);

  const toggleSort = (key) => {
    if (key === "del" || key === "notes") return;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const exportCsv = () => {
    const headers = COLS.filter((c) => c.key !== "del" && c.key !== "notes").map((c) => c.label || c.key);
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      const vals = [
        r.pool, r.division, r.team_name, r.coach_first, r.coach_last, r.email, r.phone,
        r.schedule_request ? "Yes" : "No",
        (r.extras?.extras || []).map((e) => `${e.name} x${e.qty ?? 1}`).join("; "),
        fmtDay(r.created_at), r.payment_status, r.total, r.paid, r.owed,
        r.refunded ?? r.total_amount_refunded ?? 0,
        r.scheduling_tier_fee, r.special_request_amount, r.late_fee_charged ?? 0,
        r.pay_type, r.roster_player_count,
      ];
      lines.push(vals.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event_${(data?.event?.name || "sheet").replace(/\s+/g, "_")}_${fmtDay(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* Render states */
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading sheet…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="rounded-lg border bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-800">Sheet unavailable</p>
          <p className="mt-1 text-sm text-slate-500">
            {error || "This link is invalid or has expired."}
          </p>
        </div>
      </div>
    );
  }

  if (needsGate) {
    return (
      <EmailGate
        allowedEmails={data.allowed_emails || []}
        eventName={data.event?.name}
        onPassed={() => setGatePassed(true)}
      />
    );
  }

  const ev = data.event || {};
  const venueLine = [
    ev.venue_name,
    [ev.venue_city, ev.venue_state].filter(Boolean).join(", "),
    fmtRange(ev.start_date, ev.end_date),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={embedded ? "flex flex-col bg-white" : "h-screen flex flex-col overflow-hidden bg-white"}>
      {/* Header (hidden in embedded mode — inbox supplies the title) */}
      {!embedded && (
        <div className="px-6 py-4 text-white flex items-center justify-between" style={{ backgroundColor: CYAN }}>
          <div>
            <h1 className="text-xl font-bold leading-tight">{ev.name}</h1>
            <p className="text-sm text-white/90">{venueLine}</p>
          </div>
          <div className="flex items-center gap-3">
            <PresenceBar viewers={viewers} />
            {permission === "edit" && (
              <button
                className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30"
                onClick={() => setShowAddTeam(true)}
              >
                <Plus className="h-4 w-4" /> Add Team
              </button>
            )}
            <button
              className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30"
              onClick={exportCsv}
            >
              <Download className="h-4 w-4" /> CSV
            </button>
            <button
              className="inline-flex items-center gap-1 rounded bg-white/20 px-3 py-1.5 text-sm font-medium hover:bg-white/30"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      )}

      {/* SummaryBar */}
      <div className="px-6 py-2 text-sm text-white flex flex-wrap gap-x-6 gap-y-1" style={{ backgroundColor: "rgba(6,182,212,0.9)" }}>
        <span>Teams: {summary.teams}</span>
        <span>Paid: {summary.paidCount}</span>
        <span>Comped: {summary.compedCount}</span>
        <span>Pending: {summary.pendingCount}</span>
        <span>Collected: {fmtMoney(summary.collected)}</span>
        <span>Owed: {fmtMoney(summary.owed)}</span>
        <span>
          Rosters: {summary.rostersReady}✓ / {summary.rostersPending} pending
        </span>
        {Object.entries(summary.byPool).map(([pool, n]) => (
          <span key={pool} className="rounded bg-white/20 px-2">
            {pool}: {n}
          </span>
        ))}
      </div>

      {/* Filter row */}
      <div className="bg-slate-50 px-6 py-2 flex gap-3 items-center border-b">
        <input
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Search team / coach / email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={poolFilter}
          onChange={(e) => setPoolFilter(e.target.value)}
        >
          {pools.map((p) => (
            <option key={p} value={p}>
              {p === "All" ? "All Pools" : p}
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={paidFilter}
          onChange={(e) => setPaidFilter(e.target.value)}
        >
          <option value="All">All</option>
          {PAYMENT_STATUS_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">
          {filtered.length} of {registrations.length} teams
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="border-collapse min-w-max text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>
              {COLS.map((c) => {
                if (c.editOnly && permission !== "edit") return null;
                return (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`${c.width} border px-2 py-2 text-left font-semibold text-slate-700 ${
                      c.key !== "del" && c.key !== "notes" ? "cursor-pointer select-none hover:bg-slate-200" : ""
                    }`}
                  >
                    {c.label}
                    {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-slate-50/60" : "bg-white"}>
                {COLS.map((c) => {
                  if (c.editOnly && permission !== "edit") return null;
                  const cls = `${c.width} border px-2 py-1 align-top`;

                  if (c.key === "del") {
                    return (
                      <td key="del" className={cls}>
                        <div className="relative">
                          <button
                            className="text-slate-400 hover:text-red-600"
                            onClick={() => setConfirmDel(r.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {confirmDel === r.id && (
                            <div className="absolute left-0 z-30 mt-1 w-44 rounded-md border bg-white p-2 shadow-lg">
                              <p className="text-xs text-slate-600 mb-2">Delete this team?</p>
                              <div className="flex gap-2">
                                <button
                                  className="flex-1 rounded bg-red-600 px-2 py-1 text-xs text-white"
                                  onClick={() => deleteRow(r.id)}
                                >
                                  Delete
                                </button>
                                <button
                                  className="flex-1 rounded border px-2 py-1 text-xs"
                                  onClick={() => setConfirmDel(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  }

                  if (c.editable) {
                    return (
                      <td key={c.key} className={cls}>
                        <EditableCell
                          value={r[c.key]}
                          regId={r.id}
                          field={c.field}
                          type={c.type}
                          permission={permission}
                          onUpdate={onUpdate}
                        />
                      </td>
                    );
                  }

                  if (c.dropdown === "pool") {
                    return (
                      <td key={c.key} className={cls}>
                        {permission === "edit" ? (
                          <select
                            className="rounded border bg-background px-1 py-0.5 text-xs"
                            value={r.pool ?? ""}
                            onChange={async (e) => {
                              const val = e.target.value || null;
                              onUpdate(r.id, "pool", val);
                              const { error } = await supabase
                                .from("event_registrations_v2")
                                .update({ pool: val })
                                .eq("id", r.id);
                              if (error) onUpdate(r.id, "pool", r.pool);
                            }}
                          >
                            <option value="">—</option>
                            {poolOptions.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="truncate block">{r.pool || "—"}</span>
                        )}
                      </td>
                    );
                  }


                  if (c.dropdown === "paystatus") {
                    return (
                      <td key={c.key} className={cls}>
                        {permission === "edit" ? (
                          <select
                            className="rounded border bg-background px-1 py-0.5 text-xs"
                            value={r.payment_status ?? ""}
                            onChange={(e) =>
                              updateDropdown(r.id, "payment_status", "payment_status", e.target.value)
                            }
                          >
                            {PAYMENT_STATUS_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`rounded px-2 py-0.5 text-xs ${statusBadgeClass(r.payment_status)}`}>
                            {r.payment_status || "—"}
                          </span>
                        )}
                      </td>
                    );
                  }

                  if (c.dropdown === "paytype") {
                    return (
                      <td key={c.key} className={cls}>
                        {permission === "edit" ? (
                          <select
                            className="rounded border bg-background px-1 py-0.5 text-xs"
                            value={r.pay_type ?? ""}
                            onChange={(e) =>
                              updateDropdown(r.id, "payment_method_chosen", "pay_type", e.target.value)
                            }
                          >
                            <option value="">—</option>
                            {PAY_TYPE_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`rounded px-2 py-0.5 text-xs ${payTypeBadgeClass(r.pay_type)}`}>
                            {r.pay_type || "—"}
                          </span>
                        )}
                      </td>
                    );
                  }

                  if (c.key === "schedule_request") {
                    return (
                      <td key={c.key} className={cls}>
                        {r.schedule_request ? "✓" : "—"}
                      </td>
                    );
                  }

                  if (c.key === "extras") {
                    return (
                      <td key={c.key} className={cls}>
                        <ExtrasCell extras={r.extras} />
                      </td>
                    );
                  }

                  if (c.key === "created_at") {
                    return (
                      <td key={c.key} className={cls}>
                        {fmtDay(r.created_at)}
                      </td>
                    );
                  }

                  if (c.key === "owed") {
                    return (
                      <td key={c.key} className={cls}>
                        {fmtMoney(r.owed)}
                      </td>
                    );
                  }

                  if (c.key === "refunded") {
                    return (
                      <td key={c.key} className={cls}>
                        {fmtMoney(r.refunded ?? r.total_amount_refunded ?? 0)}
                      </td>
                    );
                  }

                  if (c.key === "late_fee_charged") {
                    return (
                      <td key={c.key} className={cls}>
                        {fmtMoney(r.late_fee_charged ?? 0)}
                      </td>
                    );
                  }

                  if (c.key === "roster") {
                    return (
                      <td key={c.key} className={cls}>
                        {r.roster_player_count || 0} players
                      </td>
                    );
                  }

                  if (c.key === "notes") {
                    return (
                      <td key={c.key} className={cls}>
                        <NotesCell
                          reg={r}
                          eventId={ev.id}
                          tokenId={data.token_id}
                          permission={permission}
                          onRefetch={fetchData}
                        />
                      </td>
                    );
                  }

                  return (
                    <td key={c.key} className={cls}>
                      <span className="truncate block">{r[c.key] ?? "—"}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={COLS.length} className="border px-4 py-8 text-center text-slate-400">
                  No teams match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAddTeam && permission === "edit" && (
        <AddTeamModal
          token={token}
          poolOptions={poolOptions}
          onClose={() => setShowAddTeam(false)}
          onAdded={fetchData}
        />
      )}
    </div>
  );
}
